// The home page hero's honeycomb (HiveField.jsx) as plain geometry: pointy-top hexagons
// in offset rows over the whole field, which of them bees may pick on their own, and
// which cell a point is in.

/** The size of a cell: smaller on phones, so the band under the copy holds enough of them. */
export const cellRadius = (width) => (width < 640 ? 26 : 40);

/** How many bees work at once without anyone clicking. */
export const busyLimit = (width) => (width < 640 ? 3 : width < 1024 ? 4 : 5);

/** Room a status pill under a cell takes: half its widest (hive.css max-width) and two lines. */
const PILL_HALF = 150;
const PILL_HEIGHT = 56;

/** Every cell that touches a W x H field, row by row (each row is offset by half a cell). */
export function layoutCells(W, H, R) {
  const w = Math.sqrt(3) * R;
  const cells = [];
  for (let row = 0, y = R * 0.6; y < H + R; row += 1, y += 1.5 * R) {
    for (let x = (row % 2 ? w / 2 : 0) - w / 2; x < W + w; x += w) {
      cells.push({ x, y, state: 'empty', p: 0, glow: 0, born: 0, free: false });
    }
  }
  return cells;
}

/** The cell whose center is nearest to (x, y): on a honeycomb, the cell the point is in. */
export function nearestCell(cells, x, y) {
  let best = null;
  let bestD = Infinity;
  cells.forEach((c) => {
    const d = (c.x - x) ** 2 + (c.y - y) ** 2;
    if (d < bestD) {
      bestD = d;
      best = c;
    }
  });
  return best;
}

/**
 * One box around the rects that are laid out (client rects, made relative to `origin`),
 * or null when none is. Direction-free: in a right-to-left page it simply lands on the right.
 */
export function unionBox(rects, origin = { x: 0, y: 0 }) {
  const seen = rects.filter((k) => k && k.width > 0 && k.height > 0);
  if (!seen.length) return null;
  return {
    l: Math.min(...seen.map((k) => k.left)) - origin.x,
    r: Math.max(...seen.map((k) => k.right)) - origin.x,
    t: Math.min(...seen.map((k) => k.top)) - origin.y,
    b: Math.max(...seen.map((k) => k.bottom)) - origin.y,
  };
}

/**
 * Marks the cells bees may pick on their own: not under the copy (`box`, in field
 * coordinates, with room for a pill around it) and not cut by the field's edges. Cells
 * under the copy still light up and take clicks; bees only plan around the words.
 */
export function markFree(cells, { W, H, R, box }) {
  cells.forEach((c) => {
    const underCopy = !!box && c.x > box.l - PILL_HALF && c.x < box.r + PILL_HALF
      && c.y > box.t - R - PILL_HEIGHT && c.y < box.b + R * 0.8;
    c.free = !underCopy && c.y > R * 1.4 && c.y < H - R * 1.8 && c.x > R && c.x < W - R;
  });
}

/** Free, empty cells far enough from the cells bees already have that their pills don't touch. */
export function freeCells(cells, taken, R) {
  return cells.filter((c) => c.free && c.state === 'empty'
    && !taken.some((t) => Math.hypot(t.x - c.x, t.y - c.y) < R * 4));
}
